import { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import ImportModal from '../components/ImportModal';
import EmptyState from '../components/EmptyState';
import { IconReceipt, IconTrendingUp, IconAlertTriangle, IconClipboardCheck } from '../components/icons';
import { useCompany } from '../context/CompanyContext';
import { fyLabel, getCurrentFyStartYear } from '../utils/fy';
import api from '../api';
import './Dashboard.css';
import './DataEntry.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function inr(amount) {
  return `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getCurrentQuarter(month) {
  if (month >= 4 && month <= 6) return 'Q1';
  if (month >= 7 && month <= 9) return 'Q2';
  if (month >= 10 && month <= 12) return 'Q3';
  return 'Q4';
}

const RETURN_TYPE_LABELS = {
  '24Q': 'Salary (24Q / 138)',
  '26Q': 'Non-Salary (26Q / 140)',
  '27Q': 'Non-Resident (27Q / 144)',
};

function quarterOptions(fyStartYear) {
  return [
    { key: 'Q1', label: `Q1: April - June ${fyStartYear}` },
    { key: 'Q2', label: `Q2: July - September ${fyStartYear}` },
    { key: 'Q3', label: `Q3: October - December ${fyStartYear}` },
    { key: 'Q4', label: `Q4: January - March ${fyStartYear + 1}` },
  ];
}

const EMPTY_CHALLAN = { date_of_deposit: '', amount_deposited: '', challan_number: '', bsr_code: '' };

function DepositModal({ item, onClose, onConfirm, busy }) {
  const [challans, setChallans] = useState([EMPTY_CHALLAN]);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (item?.challans?.length) {
      setChallans(
        item.challans.map((c) => ({
          date_of_deposit: c.date_of_deposit,
          amount_deposited: c.amount_deposited,
          challan_number: c.challan_number || '',
          bsr_code: c.bsr_code || '',
        }))
      );
    } else {
      setChallans([EMPTY_CHALLAN]);
    }
    setLocalError(null);
  }, [item]);

  if (!item) return null;
  const isEditing = item.status === 'Deposited';

  const updateChallan = (idx, field, value) => {
    setChallans((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const addChallan = () => setChallans((prev) => [...prev, EMPTY_CHALLAN]);
  const removeChallan = (idx) => setChallans((prev) => prev.filter((_, i) => i !== idx));

  const validChallans = challans.filter((c) => c.date_of_deposit && c.amount_deposited !== '');
  const canSubmit = validChallans.length > 0;
  const total = validChallans.reduce((sum, c) => sum + (Number(c.amount_deposited) || 0), 0);

  const handleSubmit = () => {
    setLocalError(null);
    const result = onConfirm({
      challans: validChallans.map((c) => ({
        date_of_deposit: c.date_of_deposit,
        amount_deposited: Number(c.amount_deposited),
        challan_number: c.challan_number || null,
        bsr_code: c.bsr_code || null,
      })),
    });
    if (result && typeof result.catch === 'function') {
      result.catch((err) => setLocalError(err.response?.data?.detail || err.message || 'Could not save'));
    }
  };

  return (
    <div className="cal-modal-overlay" onClick={onClose}>
      <div className="cal-modal tds-challan-modal" onClick={(e) => e.stopPropagation()}>
        <h3>
          {isEditing ? 'Edit TDS Deposit' : 'Mark TDS Deposited'} — {MONTH_NAMES[item.period_month - 1]} {item.period_year}
        </h3>
        <p className="dash-table-sub" style={{ marginBottom: 12 }}>
          Paid across more than one challan this month? Add a row for each — the total below is what counts toward this
          period's liability.
        </p>
        {localError && <p className="dash-error">{localError}</p>}

        <div className="tds-challan-list">
          <div className="tds-challan-header-row">
            <span>Date</span>
            <span>Amount ₹</span>
            <span>Challan No.</span>
            <span>BSR Code</span>
            <span />
          </div>
          {challans.map((c, idx) => (
            <div className="tds-challan-row" key={idx}>
              <input
                type="date"
                className="field-input"
                value={c.date_of_deposit}
                onChange={(e) => updateChallan(idx, 'date_of_deposit', e.target.value)}
              />
              <input
                type="number"
                className="field-input"
                value={c.amount_deposited}
                onChange={(e) => updateChallan(idx, 'amount_deposited', e.target.value)}
              />
              <input
                className="field-input"
                value={c.challan_number}
                onChange={(e) => updateChallan(idx, 'challan_number', e.target.value)}
              />
              <input
                className="field-input"
                value={c.bsr_code}
                onChange={(e) => updateChallan(idx, 'bsr_code', e.target.value)}
              />
              <button
                type="button"
                className="dash-btn-red-outline tds-challan-remove"
                onClick={() => removeChallan(idx)}
                disabled={challans.length === 1}
                title="Remove this challan"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <button type="button" className="dash-btn cal-btn-grey" style={{ marginTop: 8 }} onClick={addChallan}>
          + Add another challan
        </button>

        <p className="tds-challan-total">Total Deposited: {inr(total)}</p>

        <div className="cal-modal-actions">
          <button className="dash-btn cal-btn-grey" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="dash-btn dash-btn-orange" type="button" disabled={busy || !canSubmit} onClick={handleSubmit}>
            {busy ? 'Saving...' : isEditing ? 'Save Changes' : 'Mark as Deposited'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilingModal({ item, onClose, onConfirm, busy }) {
  const [dateOfFiling, setDateOfFiling] = useState('');
  const [ackNumber, setAckNumber] = useState('');
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    setDateOfFiling(item?.date_of_filing || '');
    setAckNumber(item?.acknowledgment_number || '');
    setLocalError(null);
  }, [item]);

  if (!item) return null;
  const isEditing = item.status === 'Filed';
  const canSubmit = Boolean(dateOfFiling);

  const handleSubmit = () => {
    setLocalError(null);
    const result = onConfirm({
      date_of_filing: dateOfFiling,
      acknowledgment_number: ackNumber || null,
    });
    if (result && typeof result.catch === 'function') {
      result.catch((err) => setLocalError(err.response?.data?.detail || err.message || 'Could not save'));
    }
  };

  return (
    <div className="cal-modal-overlay" onClick={onClose}>
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <h3>
          {isEditing ? 'Edit TDS Return Filing' : 'Mark TDS Return as Filed'} — {item.quarter}{' '}
          {RETURN_TYPE_LABELS[item.return_type] || item.return_type}
        </h3>
        {localError && <p className="dash-error">{localError}</p>}
        <label className="cal-modal-label">
          Date of filing
          <input
            type="date"
            className="cal-modal-date"
            value={dateOfFiling}
            onChange={(e) => setDateOfFiling(e.target.value)}
          />
        </label>
        <div className="field-group">
          <span className="field-label">Acknowledgment Number</span>
          <input className="field-input" value={ackNumber} onChange={(e) => setAckNumber(e.target.value)} />
        </div>
        <div className="cal-modal-actions">
          <button className="dash-btn cal-btn-grey" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="dash-btn dash-btn-orange" type="button" disabled={busy || !canSubmit} onClick={handleSubmit}>
            {busy ? 'Saving...' : isEditing ? 'Save Changes' : 'Mark as Filed'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TdsTcs() {
  const { selectedCompanyId, fyStartYear } = useCompany();
  const today = new Date();
  const isRealCurrentFy = fyStartYear === getCurrentFyStartYear();

  const [activeTab, setActiveTab] = useState('quarterly');
  const [quarter, setQuarter] = useState(isRealCurrentFy ? getCurrentQuarter(today.getMonth() + 1) : 'Q1');

  const [tdsRows, setTdsRows] = useState([]);
  const [ytdData, setYtdData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [deposits, setDeposits] = useState([]);
  const [filings, setFilings] = useState([]);
  const [depositModalItem, setDepositModalItem] = useState(null);
  const [filingModalItem, setFilingModalItem] = useState(null);
  const [savingDeposit, setSavingDeposit] = useState(false);
  const [savingFiling, setSavingFiling] = useState(false);
  const [importDepositOpen, setImportDepositOpen] = useState(false);
  const [importFilingOpen, setImportFilingOpen] = useState(false);

  const loadDeposits = () => {
    if (!selectedCompanyId) return;
    api.get(`/tds-deposits/${selectedCompanyId}`, { params: { fy_year: fyStartYear } }).then((res) => setDeposits(res.data));
  };

  const loadFilings = () => {
    if (!selectedCompanyId) return;
    api.get(`/tds-return-filings/${selectedCompanyId}`, { params: { fy_year: fyStartYear } }).then((res) => setFilings(res.data));
  };

  useEffect(() => {
    loadDeposits();
    loadFilings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, fyStartYear]);

  const confirmDeposit = (payload) => {
    if (!depositModalItem) return undefined;
    setSavingDeposit(true);
    return api
      .post(`/tds-deposits/${depositModalItem.id}/deposit`, payload)
      .then(() => {
        setDepositModalItem(null);
        loadDeposits();
      })
      .finally(() => setSavingDeposit(false));
  };

  const confirmFiling = (payload) => {
    if (!filingModalItem) return undefined;
    setSavingFiling(true);
    return api
      .post(`/tds-return-filings/${filingModalItem.id}/file`, payload)
      .then(() => {
        setFilingModalItem(null);
        loadFilings();
      })
      .finally(() => setSavingFiling(false));
  };

  useEffect(() => {
    if (!selectedCompanyId || activeTab !== 'quarterly') return;
    setLoading(true);
    api
      .get(`/tds/${selectedCompanyId}`, { params: { quarter, fy_year: fyStartYear } })
      .then((res) => {
        setTdsRows(res.data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedCompanyId, quarter, fyStartYear, activeTab]);

  useEffect(() => {
    if (!selectedCompanyId || activeTab !== 'ytd') return;
    setLoading(true);
    api
      .get(`/tds/${selectedCompanyId}/ytd`, { params: { fy_year: fyStartYear } })
      .then((res) => {
        setYtdData(res.data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedCompanyId, fyStartYear, activeTab]);

  const totalDeducted = tdsRows.reduce((sum, r) => sum + r.tds_amount, 0);
  // Deposits are tracked monthly and company-wide (TdsDeposit), not
  // per-section (TdsData has no accurate way to represent "was section
  // 194C's share of a combined monthly payment deposited") - so this
  // reads from the real deposit records for this quarter's months,
  // not TdsData.deposit_status (which no code path ever sets away from
  // its "Pending" default - see get_tds_ytd's fix in main.py for the
  // same bug on the backend YTD aggregate).
  const depositedAmt = deposits
    .filter((d) => d.quarter === quarter && d.status === 'Deposited')
    .reduce((sum, d) => sum + (d.amount_deposited || 0), 0);
  const pendingDeposit = totalDeducted - depositedAmt;

  return (
    <PageLayout>
      <div className="page-title-row">
        <div>
          <h1 className="page-title">TDS / TCS Management</h1>
          <p className="page-subtitle">Auto-calculated from vendor and salary payments — {fyLabel(fyStartYear)}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="dash-btn cal-btn-grey" type="button" onClick={() => setImportDepositOpen(true)}>
            Import Deposits
          </button>
          <button className="dash-btn cal-btn-grey" type="button" onClick={() => setImportFilingOpen(true)}>
            Import Return Filings
          </button>
        </div>
      </div>

      {error && <p className="dash-error">Backend unreachable: {error}</p>}

      <div className="de-tabs">
        <button type="button" className={`de-tab ${activeTab === 'quarterly' ? 'active' : ''}`} onClick={() => setActiveTab('quarterly')}>
          Quarterly
        </button>
        <button type="button" className={`de-tab ${activeTab === 'ytd' ? 'active' : ''}`} onClick={() => setActiveTab('ytd')}>
          YTD
        </button>
      </div>

      <div className={loading ? 'dash-refreshing' : ''}>
        {activeTab === 'quarterly' && (
          <>
            <div className="field-group" style={{ maxWidth: 320, marginBottom: 20 }}>
              <span className="field-label">Quarter</span>
              <select className="field-select" value={quarter} onChange={(e) => setQuarter(e.target.value)}>
                {quarterOptions(fyStartYear).map((q) => (
                  <option key={q.key} value={q.key}>{q.label}</option>
                ))}
              </select>
            </div>

            <div className="dash-stat-grid">
              <div className="dash-stat-card">
                <div className="dash-stat-icon orange"><IconReceipt /></div>
                <div className="dash-stat-value dash-serif">{inr(totalDeducted)}</div>
                <div className="dash-stat-label">TDS Deducted {quarter}</div>
              </div>
              <div className="dash-stat-card">
                <div className="dash-stat-icon green"><IconTrendingUp /></div>
                <div className="dash-stat-value dash-serif green">{inr(depositedAmt)}</div>
                <div className="dash-stat-label">Deposited</div>
              </div>
              <div className="dash-stat-card">
                <div className={`dash-stat-icon ${pendingDeposit > 0 ? 'red' : 'grey'}`}><IconAlertTriangle /></div>
                <div className={`dash-stat-value dash-serif ${pendingDeposit > 0 ? 'red' : 'grey'}`}>
                  {inr(pendingDeposit)}
                </div>
                <div className="dash-stat-label">Pending Deposit</div>
              </div>
              <div className="dash-stat-card">
                <div className="dash-stat-icon grey"><IconClipboardCheck /></div>
                <div className="dash-stat-value dash-serif grey" style={{ fontSize: 20 }}>Coming Soon</div>
                <div className="dash-stat-label">Form 26AS Match</div>
                <div className="dash-stat-sub">Reconciliation not yet built</div>
              </div>
            </div>

            <div className="dash-card">
              <h2 className="dash-card-title">Section-wise deduction tracker — {quarter} {fyLabel(fyStartYear)}</h2>
              <p className="dash-table-sub" style={{ marginBottom: 16 }}>
                Deposits are tracked monthly, not per section (one payment covers all sections together) - see the
                TDS Deposits table on the YTD tab for actual deposit status and dates.
              </p>
              {tdsRows.length === 0 ? (
                <EmptyState
                  icon={IconReceipt}
                  message="No TDS data entered for this quarter."
                  ctaLabel="Go to Data Entry"
                  to="/data-entry"
                />
              ) : (
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Section</th>
                      <th>Nature</th>
                      <th>Gross Amount</th>
                      <th>Rate</th>
                      <th>TDS Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tdsRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.section}</td>
                        <td>{row.nature}</td>
                        <td className="dash-serif">{inr(row.gross_amount)}</td>
                        <td className="dash-serif">{row.rate === null ? 'Slab' : `${row.rate}%`}</td>
                        <td className="dash-serif">{inr(row.tds_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="dash-btn-row">
                <button className="dash-btn dash-btn-outline" type="button">Ask AI ↗</button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'ytd' && ytdData && (
          <>
            <div className="dash-stat-grid dash-stat-grid-3">
              <div className="dash-stat-card">
                <div className="dash-stat-icon orange"><IconReceipt /></div>
                <div className="dash-stat-value dash-serif">{inr(ytdData.total_deducted)}</div>
                <div className="dash-stat-label">Total TDS Deducted</div>
              </div>
              <div className="dash-stat-card">
                <div className="dash-stat-icon green"><IconTrendingUp /></div>
                <div className="dash-stat-value dash-serif green">{inr(ytdData.total_deposited)}</div>
                <div className="dash-stat-label">Total Deposited</div>
              </div>
              <div className="dash-stat-card">
                <div className={`dash-stat-icon ${ytdData.total_pending > 0 ? 'red' : 'grey'}`}><IconAlertTriangle /></div>
                <div className={`dash-stat-value dash-serif ${ytdData.total_pending > 0 ? 'red' : 'grey'}`}>
                  {inr(ytdData.total_pending)}
                </div>
                <div className="dash-stat-label">Total Pending</div>
              </div>
            </div>

            <div className="dash-card">
              <h2 className="dash-card-title">Quarter-wise — {fyLabel(fyStartYear)}</h2>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Quarter</th>
                    <th>Deducted</th>
                    <th>Deposited</th>
                    <th>Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {ytdData.quarters.map((q) => (
                    <tr key={q.quarter}>
                      <td>{q.quarter} ({q.year})</td>
                      <td className="dash-serif">{inr(q.deducted)}</td>
                      <td className="dash-serif">{inr(q.deposited)}</td>
                      <td className="dash-serif">{inr(q.pending)}</td>
                    </tr>
                  ))}
                  <tr className="dash-table-total">
                    <td>Total</td>
                    <td className="dash-serif">{inr(ytdData.total_deducted)}</td>
                    <td className="dash-serif">{inr(ytdData.total_deposited)}</td>
                    <td className="dash-serif">{inr(ytdData.total_pending)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="dash-card">
          <h2 className="dash-card-title">TDS Deposits (Monthly) — {fyLabel(fyStartYear)}</h2>
          <p className="dash-table-sub" style={{ marginBottom: 12 }}>
            Due 7th of the month following deduction. Linked to its quarter's Return Filing below,
            but penalty is calculated independently for each.
          </p>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Quarter</th>
                <th>Due Date</th>
                <th>Date of Deposit</th>
                <th>Challan / BSR</th>
                <th>Status</th>
                <th>Penalty</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deposits.length === 0 && (
                <tr><td colSpan={8} className="dash-empty">No TDS deposits for this FY.</td></tr>
              )}
              {deposits.map((d) => (
                <tr key={d.id} style={d.is_applicable === false ? { opacity: 0.55 } : undefined}>
                  <td>{MONTH_NAMES[d.period_month - 1]} {d.period_year}</td>
                  <td>{d.quarter}</td>
                  <td>{formatDate(d.due_date)}</td>
                  <td>{formatDate(d.date_of_deposit)}</td>
                  <td>
                    {d.challans && d.challans.length > 1
                      ? `${d.challans.length} challans`
                      : `${d.challan_number || '—'}${d.bsr_code ? ` / ${d.bsr_code}` : ''}`}
                  </td>
                  <td>
                    {d.is_applicable === false ? (
                      <span className="dash-pill dash-pill-blue">N/A</span>
                    ) : (
                      <span className={`dash-pill ${d.status === 'Deposited' ? 'dash-pill-green' : 'dash-pill-amber'}`}>
                        {d.status}
                      </span>
                    )}
                  </td>
                  <td className={d.is_applicable !== false && d.penalty_amount > 0 ? 'red' : ''}>
                    {d.is_applicable === false ? '—' : d.penalty_amount > 0 ? inr(d.penalty_amount) : '—'}
                  </td>
                  <td>
                    {d.is_applicable !== false && (
                      <button className="dash-btn cal-btn-grey" type="button" onClick={() => setDepositModalItem(d)}>
                        {d.status === 'Deposited' ? 'Edit' : 'Mark Deposited'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="dash-card">
          <h2 className="dash-card-title">TDS Return Filings (Quarterly) — {fyLabel(fyStartYear)}</h2>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Quarter</th>
                <th>Type</th>
                <th>Due Date</th>
                <th>Date of Filing</th>
                <th>Acknowledgment No.</th>
                <th>Status</th>
                <th>Penalty</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filings.length === 0 && (
                <tr><td colSpan={8} className="dash-empty">No TDS return filings for this FY.</td></tr>
              )}
              {filings.map((f) => (
                <tr key={f.id} style={f.is_applicable === false ? { opacity: 0.55 } : undefined}>
                  <td>{f.quarter}</td>
                  <td>{RETURN_TYPE_LABELS[f.return_type] || f.return_type}</td>
                  <td>{formatDate(f.due_date)}</td>
                  <td>{formatDate(f.date_of_filing)}</td>
                  <td>{f.acknowledgment_number || '—'}</td>
                  <td>
                    {f.is_applicable === false ? (
                      <span className="dash-pill dash-pill-blue">N/A</span>
                    ) : (
                      <span className={`dash-pill ${f.status === 'Filed' ? 'dash-pill-green' : 'dash-pill-amber'}`}>
                        {f.status}
                      </span>
                    )}
                  </td>
                  <td className={f.is_applicable !== false && f.penalty_amount > 0 ? 'red' : ''}>
                    {f.is_applicable === false ? '—' : f.penalty_amount > 0 ? inr(f.penalty_amount) : '—'}
                  </td>
                  <td>
                    <button className="dash-btn cal-btn-grey" type="button" onClick={() => setFilingModalItem(f)}>
                      {f.status === 'Filed' ? 'Edit' : 'Mark Filed'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DepositModal
        item={depositModalItem}
        onClose={() => setDepositModalItem(null)}
        onConfirm={confirmDeposit}
        busy={savingDeposit}
      />
      <FilingModal
        item={filingModalItem}
        onClose={() => setFilingModalItem(null)}
        onConfirm={confirmFiling}
        busy={savingFiling}
      />

      <ImportModal
        open={importDepositOpen}
        onClose={() => setImportDepositOpen(false)}
        module="tds_deposit"
        moduleLabel="TDS Deposit"
        companyId={selectedCompanyId}
        onImported={loadDeposits}
      />
      <ImportModal
        open={importFilingOpen}
        onClose={() => setImportFilingOpen(false)}
        module="tds_return_filing"
        moduleLabel="TDS Return Filing"
        companyId={selectedCompanyId}
        onImported={loadFilings}
      />
    </PageLayout>
  );
}

export default TdsTcs;
